/**
 * The Fidèle header (sections/fidele-header.liquid): the phone drawer, the desktop panels' state for
 * assistive technology (the panels themselves open by CSS, on hover and on focus), Escape to close a
 * panel, and the bag's live count from Shopify's cart events.
 */
class FideleHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('#FdlDrawer');
    this.burger = this.querySelector('[data-fdl-open]');
    this.count = this.querySelector('[data-fdl-count]');
    this.bag = this.querySelector('[data-fdl-bag]');

    this.burger?.addEventListener('click', this.openDrawer);
    // DECISIONS row 44: the cart icon opens the cart drawer (the link to /cart stays the fallback)
    this.bag?.addEventListener('click', (event) => {
      const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
    });
    this.querySelectorAll('[data-fdl-close]').forEach((button) => button.addEventListener('click', this.closeDrawer));
    this.drawer?.addEventListener('click', this.onDrawerClick);
    this.drawer?.addEventListener('close', this.onDrawerClose);

    this.querySelectorAll('[data-fdl-mega]').forEach((item) => {
      const link = item.querySelector('.fdl-hd__link');
      const set = (open) => link?.setAttribute('aria-expanded', String(open));
      item.addEventListener('mouseenter', () => set(true));
      item.addEventListener('mouseleave', () => {
        set(false);
        item.classList.remove('is-closed');
      });
      item.addEventListener('focusin', () => {
        if (!item.classList.contains('is-closed')) set(true);
      });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(/** @type {Node} */ (event.relatedTarget))) {
          set(false);
          item.classList.remove('is-closed');
        }
      });
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        item.classList.add('is-closed');
        set(false);
        link?.focus();
      });
    });

    document.addEventListener('shopify:cart:lines-update', this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  openDrawer = () => {
    const drawer = /** @type {HTMLDialogElement | null} */ (this.drawer);
    if (!drawer || typeof drawer.showModal !== 'function') return;
    drawer.showModal();
    this.burger?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('fdl-drawer-open');
  };

  closeDrawer = () => {
    /** @type {HTMLDialogElement | null} */ (this.drawer)?.close();
  };

  onDrawerClick = (event) => {
    // a click on the backdrop (the dialog itself, outside its content) closes it
    if (event.target === this.drawer) this.closeDrawer();
  };

  onDrawerClose = () => {
    this.burger?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('fdl-drawer-open');
  };

  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => this.setCount(cart?.totalQuantity ?? detail?.itemCount ?? 0))
      .catch(() => {});
  };

  onPageShow = (event) => {
    if (event.persisted) this.refresh();
  };

  refresh = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      this.setCount(cart.item_count ?? 0);
    } catch (error) {
      // the count stays as rendered
    }
  };

  setCount(count) {
    if (this.count) this.count.textContent = String(count);
    if (this.bag) {
      const label = (this.bag.getAttribute('aria-label') || '').replace(/\s*\(\d+\)$/, '');
      this.bag.setAttribute('aria-label', `${label} (${count})`);
    }
  }
}

if (!customElements.get('fidele-header')) customElements.define('fidele-header', FideleHeader);

/**
 * The Day S announcement (sections/fidele-header.liquid, DECISIONS row 36): one message at a time, the next every
 * 5 seconds and on the arrow. It holds still while the pointer is over it or focus is in it, while the tab is
 * hidden, and for visitors who ask for reduced motion (the arrow still steps).
 */
class FideleAnn extends HTMLElement {
  connectedCallback() {
    this.msgs = Array.from(this.querySelectorAll('.fdl-ann2__msg'));
    this.list = this.querySelector('.fdl-ann2__list');
    if (this.msgs.length < 2) return;
    this.index = 0;
    this.held = false;
    this.still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelector('[data-fdl-ann-next]')?.addEventListener('click', this.onNext);
    this.addEventListener('mouseenter', this.hold);
    this.addEventListener('mouseleave', this.release);
    this.addEventListener('focusin', this.hold);
    this.addEventListener('focusout', this.release);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  start() {
    this.stop();
    if (this.still || this.held || document.hidden) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), 5000);
  }

  stop() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = undefined;
  }

  show(index) {
    this.index = index % this.msgs.length;
    this.msgs.forEach((msg, i) => {
      msg.hidden = i !== this.index;
      msg.classList.toggle('is-on', i === this.index);
    });
  }

  onNext = () => {
    // a step the visitor asked for is read out
    this.list?.setAttribute('aria-live', 'polite');
    this.show(this.index + 1);
  };

  hold = () => {
    this.held = true;
    this.stop();
  };

  release = (event) => {
    if (event.type === 'focusout' && this.contains(/** @type {Node} */ (event.relatedTarget))) return;
    if (event.type === 'mouseleave' && this.contains(document.activeElement)) return;
    this.held = false;
    this.list?.setAttribute('aria-live', 'off');
    this.start();
  };

  onVisibility = () => {
    if (document.hidden) this.stop();
    else this.start();
  };
}

if (!customElements.get('fidele-ann')) customElements.define('fidele-ann', FideleAnn);

// DECISIONS row 44: Add to cart shows a short loading state until the cart answers, then the drawer opens
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    const button = form.querySelector('.add-to-cart-button') || document.querySelector('.product-information .add-to-cart-button');
    if (!button) return;
    button.classList.add('fdl-is-loading');
    // until the cart drawer opens (or 4 seconds at most)
    const started = Date.now();
    const timer = window.setInterval(() => {
      const dialog = document.querySelector('#cart-drawer dialog');
      if ((dialog && dialog.hasAttribute('open')) || Date.now() - started > 4000) {
        button.classList.remove('fdl-is-loading');
        window.clearInterval(timer);
      }
    }, 100);
  },
  true
);
document.addEventListener('cart:update', () => {
  document.querySelectorAll('.add-to-cart-button.fdl-is-loading').forEach((b) => b.classList.remove('fdl-is-loading'));
});

// DECISIONS row 44: on wide screens Horizon's cart drawer sits beside the page with no backdrop; a click
// outside it closes it, as on phones
document.addEventListener('click', (event) => {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  const dialog = drawer?.querySelector('dialog');
  if (!drawer || !dialog || !dialog.hasAttribute('open') || typeof drawer.close !== 'function') return;
  const target = event.target instanceof Element ? event.target : null;
  if (!target || dialog.contains(target)) return;
  if (target.closest('[data-fdl-bag], .add-to-cart-button, add-to-cart-component, .sticky-add-to-cart, [data-fdl-quick-add]')) return;
  drawer.close();
});

// DECISIONS row 44: "Sizes in mm" on touch screens: the first tap opens the small panel, a tap elsewhere closes it
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const link = target?.closest('[data-fdl-szpop-link]');
  const touch = window.matchMedia('(hover: none)').matches;
  document.querySelectorAll('[data-fdl-szpop].is-open').forEach((el) => {
    if (!el.contains(target)) el.classList.remove('is-open');
  });
  if (link && touch) {
    const pop = link.closest('[data-fdl-szpop]');
    if (pop && !pop.classList.contains('is-open')) {
      event.preventDefault();
      pop.classList.add('is-open');
    }
  }
});
